// client/src/pages/sectionAugments.js
// Section augments — per-period section CONFIG overrides that tweak ONE section's
// behavior for ONE cycle without a separate section definition and without
// splitting sign-offs (those stay keyed by the shared section_key + period code).
//
// An augment value persists into dataIntegrity.period_sections.config_json and is
// merged over the section's base config by diConfig, so the section's component
// can read it off its config at render time.
//
// Each augment is code-coupled — it needs matching behavior in the section's
// component (and sometimes an endpoint) — so the catalog lives here, keyed by
// section_key. Add an entry when you wire a new per-period toggle; the Periods
// admin page renders a drilldown of these for any section that has at least one.

export const SECTION_AUGMENTS = {
  'client-groups': [
    {
      key: 'officeWideVisibility',
      label: 'Office-wide visibility',
      description:
        'Users this period applies to also see Client Groups issues for clients in their own office (client office = their staff office), not just their own. Relationship-Group confirmation stays limited to the anchor partner.',
      type: 'toggle',
    },
    {
      key: 'standaloneSeed',
      label: 'Standalone-by-default (PE-only)',
      description:
        'For cycles with PE data only (no Knuula/Snowflake): every client starts as its own Signer Group and Relationship Group, Steps 1–2 surface no missing-source issues, and partners group/modify from Steps 3–4. Leave off for normal cycles.',
      type: 'toggle',
    },
  ],
  'client-contact-data': [
    {
      key: 'officeWideVisibility',
      label: 'Office-wide visibility',
      description:
        'Users this period applies to see — and can review — ALL Client Contact clients in their own office (client office = their staff office), not just their own clients.',
      type: 'toggle',
    },
    {
      key: 'newClientImport',
      label: 'New client import',
      description:
        'Adds two per-client columns: "Paper EL" (client receives paper engagement letters — stored globally as isPaper) and "Mark NLC" (mark No Longer Client — removes the client from every section and period). Anyone who can see the row can mark it.',
      type: 'toggle',
    },
    {
      key: 'excludeFriendsAndFamily',
      label: 'Exclude Friends & Family (FAM)',
      description:
        'Drops clients in the "FAM" (Friends & Family) office from the Client Contact population entirely — the section grid, its counts, the Home progress card, and the Steering rollup all exclude them.',
      type: 'toggle',
    },
    {
      key: 'cchReportingOnly',
      label: 'CCH is reporting-only',
      description:
        'Excludes CCH from every mismatch calculation (a CCH disagreement never flags, and its cells never highlight), and shows a banner noting CCH data is for reporting only — no contact data is imported into CCH.',
      type: 'toggle',
    },
    {
      key: 'sources',
      label: 'Vendor data sources',
      description:
        'Pick which blob feeds each vendor for this period (blank = the standard file: knuula.json / safesend.json / cch.json), plus how records join to PE — a match mode (Exact, or By name: case/spacing/word-order-insensitive, for name-only joins) and the source + PE columns to match on (chosen from the actual field names in each source; blank = the default: ContIndex↔ContIndex for Knuula/CCH, ClientID↔ClientCode for SafeSend). A blob can only be assigned to one vendor. Mark a vendor "Source of truth" and, wherever it has a value for a field, that value rules — no mismatch is flagged for that field. A SafeSend feed that carries spouse emails is cross-checked against PE — and can fill a missing PE spouse email.',
      type: 'vendor-source',
    },
  ],
};

export function augmentsFor(sectionKey) {
  return SECTION_AUGMENTS[sectionKey] || [];
}

// Read an augment's current value from a (possibly sparse) config object,
// tolerating the blob-JSON string-"true" trap.
export function augmentOn(config, key) {
  const v = config ? config[key] : undefined;
  return v === true || (typeof v === 'string' && v.trim().toLowerCase() === 'true');
}
